import { __read, __spreadArray, __values } from "tslib";
import { HubTileID } from '@office-start/appbar-types';
import { useCapabilities } from '@office-start/capabilities-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { useIdentity } from '@office-start/identity-service';
import { PagePaths } from '@office-start/officehome-types';
import { useIsSubscribedUser } from '@office-start/subscription-info-service';
import { useUserEntitlements } from '@office-start/user-entitlements-service';
import { useWorkloadPublicData } from '@office-start/workload-publicdata-config-service';
import { useWorkloads } from '@office-start/workloads-service';
import * as React from 'react';
import create from 'zustand';
import { getFirstPartyWorkloadId } from './getHubTileIdFromFirstPartyWorkloadId';
import { useDefaultPinSetOverride } from './useDefaultPinSetOverride';
var useDefaultHubAppsStore = create(function (set) { return ({
    hubAppData: undefined,
    setHubAppData: function (hubAppData) {
        set({ hubAppData: hubAppData });
    },
}); });
/**
 * Custom React Hook responsible for resolving default hub apps
 */
export var useDefaultHubApps = function () {
    var _a, _b, _c;
    var identity = useIdentity().data;
    var userEntitlementsResult = useUserEntitlements().data;
    var workloads = useWorkloads().data;
    var capabilities = useCapabilities().data;
    var featureGates = useFeatureGates().data;
    var workloadPublicData = useWorkloadPublicData().data;
    var defaultPinSetOverride = useDefaultPinSetOverride();
    var isSubscribedUser = useIsSubscribedUser();
    var hasConsent = ((_a = workloadPublicData === null || workloadPublicData === void 0 ? void 0 : workloadPublicData.userConsentInfo) === null || _a === void 0 ? void 0 : _a.UserConsent) ===
        0 /* ConsumerUserConsentGroup.CanConsent */;
    var _d = useDefaultHubAppsStore(), hubAppData = _d.hubAppData, setHubAppData = _d.setHubAppData;
    React.useEffect(function () {
        var e_1, _a;
        var _b, _c, _d, _e, _f, _g, _h;
        if (!identity ||
            !(userEntitlementsResult === null || userEntitlementsResult === void 0 ? void 0 : userEntitlementsResult.result) ||
            !workloads ||
            !capabilities) {
            return;
        }
        var isMsa = identity.isMsa;
        // Map { HubTileID => IsPinned: boolean }
        // Note: Use Map instead of Object to preserve insertion order upon iteration.
        var hubTiles = !isMsa
            ? // Enterprise default apps
                new Map([
                    [HubTileID.home, false],
                    [HubTileID.create, false],
                    [HubTileID.mycontent, false],
                    [HubTileID.feed, false],
                    [HubTileID.appgallery, false],
                    [HubTileID.admin, false],
                    [HubTileID.outlook, false],
                    [HubTileID.teams, false],
                    [HubTileID.word, false],
                    [HubTileID.excel, false],
                    [HubTileID.powerpoint, false],
                    [HubTileID.onenote, false],
                    [HubTileID.forms, false],
                    [HubTileID.sway, false],
                    [HubTileID.visio, false],
                    [HubTileID.stream, false],
                    [HubTileID.whiteboard, false],
                    [HubTileID.clipchamp, false],
                ])
            : // Consumer default apps
                new Map([
                    [HubTileID.home, false],
                    [HubTileID.create, false],
                    [HubTileID.mycontent, false],
                    [HubTileID.feed, false],
                    [HubTileID.appgallery, false],
                    [HubTileID.word, false],
                    [HubTileID.excel, false],
                    [HubTileID.powerpoint, false],
                    [HubTileID.outlook, false],
                    [HubTileID.onedrive, false],
                    [HubTileID.teams, false],
                    [HubTileID.onenote, false],
                    [HubTileID.todo, false],
                    [HubTileID.familysafety, false],
                    [HubTileID.calendar, false],
                    [HubTileID.skype, false],
                    [HubTileID.forms, false],
                    [HubTileID.sway, false],
                    [HubTileID.admin, false],
                    [HubTileID.people, false],
                    [HubTileID.powerautomate, false],
                    [HubTileID.clipchamp, false],
                    [HubTileID.lists, false],
                    [HubTileID.bing, false],
                    [HubTileID.msn, false],
                    [HubTileID.privacy, false],
                    [HubTileID.rewards, false],
                    [HubTileID.defender, false],
                ]);
        var isOrionEnabled = ((_c = (_b = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _b === void 0 ? void 0 : _b.orionGates) === null || _c === void 0 ? void 0 : _c.orionEnabled) || false;
        if (isOrionEnabled) {
            hubTiles.set(HubTileID.orion, false);
        }
        var _j = userEntitlementsResult.result, isAdminUser = _j.IsAdmin, isEduUser = _j.IsEdu, isEduFacultyUser = _j.IsEduFaculty;
        var defaultPinSet = defaultPinSetOverride !== null && defaultPinSetOverride !== void 0 ? defaultPinSetOverride : capabilities.appBarDefaultPinSet;
        // Refer to "Zinc App Bar Experimentation Plan" doc
        switch (defaultPinSet) {
            case 'A': // Clean App Bar
                if (isAdminUser) {
                    hubTiles.set(HubTileID.admin, true);
                }
                break;
            case 'B': // Greatest Hits
                if (isMsa) {
                    hubTiles.set(HubTileID.outlook, true);
                    hubTiles.set(HubTileID.word, true);
                    hubTiles.set(HubTileID.excel, true);
                }
                else {
                    // Commercial
                    if (isAdminUser) {
                        hubTiles.set(HubTileID.admin, true);
                    }
                    if (isEduFacultyUser) {
                        hubTiles.set(HubTileID.outlook, true);
                        hubTiles.set(HubTileID.word, true);
                        hubTiles.set(HubTileID.forms, true);
                    }
                    else if (isEduUser) {
                        hubTiles.set(HubTileID.outlook, true);
                        hubTiles.set(HubTileID.word, true);
                        hubTiles.set(HubTileID.powerpoint, true);
                    }
                    else {
                        hubTiles.set(HubTileID.outlook, true);
                        hubTiles.set(HubTileID.word, true);
                        hubTiles.set(HubTileID.excel, true);
                    }
                }
                break;
            case 'C': // Diamonds in the Rough
                if (isMsa) {
                    hubTiles.set(HubTileID.clipchamp, true);
                    hubTiles.set(HubTileID.forms, true);
                    hubTiles.set(HubTileID.lists, true);
                }
                else {
                    // Commercial
                    if (isAdminUser) {
                        hubTiles.set(HubTileID.admin, true);
                    }
                    if (isEduUser) {
                        hubTiles.set(HubTileID.onenote, true);
                        hubTiles.set(HubTileID.forms, true);
                        hubTiles.set(HubTileID.stream, true);
                    }
                    else {
                        hubTiles.set(HubTileID.visio, true);
                        hubTiles.set(HubTileID.yammer, true);
                        hubTiles.set(HubTileID.powerbi, true);
                    }
                }
                break;
            case 'D': // Familiar
            default:
                if (isMsa) {
                    hubTiles.set(HubTileID.word, true);
                    hubTiles.set(HubTileID.excel, true);
                    hubTiles.set(HubTileID.powerpoint, true);
                    hubTiles.set(HubTileID.outlook, true);
                    hubTiles.set(HubTileID.onedrive, true);
                    hubTiles.set(HubTileID.teams, true);
                    hubTiles.set(HubTileID.onenote, true);
                }
                else {
                    // Commercial
                    if (isAdminUser) {
                        hubTiles.set(HubTileID.admin, true);
                    }
                    hubTiles.set(HubTileID.outlook, true);
                    hubTiles.set(HubTileID.teams, true);
                    hubTiles.set(HubTileID.word, true);
                    hubTiles.set(HubTileID.excel, true);
                    hubTiles.set(HubTileID.powerpoint, true);
                    if (isEduUser) {
                        // OneNote should only be pinned by default for EDU users.
                        hubTiles.set(HubTileID.onenote, true);
                    }
                    if (isEduFacultyUser) {
                        // Forms should only be pinned by default for EDU/Faculty users.
                        hubTiles.set(HubTileID.forms, true);
                    }
                }
                break;
            case 'E': // New treatment for Consumers only
                if (isMsa) {
                    hubTiles.set(HubTileID.word, true);
                    hubTiles.set(HubTileID.excel, true);
                    hubTiles.set(HubTileID.powerpoint, true);
                }
                break;
        }
        var allWorkloads = __spreadArray(__spreadArray([], __read(workloads.coreAndSegmentApps), false), __read(workloads.nonCoreAndSegmentApps), false);
        var _loop_1 = function (tileID) {
            var firstPartyWorkloadId = getFirstPartyWorkloadId(tileID, isMsa);
            if (firstPartyWorkloadId) {
                var workload = allWorkloads.find(function (workload) { return workload.Id === firstPartyWorkloadId; });
                if (!workload) {
                    // Remove this tile that the user is not licensed for.
                    hubTiles.delete(tileID);
                }
            }
        };
        try {
            // Note: Use Array.from() to extract out the tile ids so we can safely delete entries in hubTiles inside a for-of loop.
            for (var _k = __values(Array.from(hubTiles.keys())), _l = _k.next(); !_l.done; _l = _k.next()) {
                var tileID = _l.value;
                _loop_1(tileID);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (_l && !_l.done && (_a = _k.return)) _a.call(_k);
            }
            finally { if (e_1) throw e_1.error; }
        }
        if (!capabilities.appBarOverflowOnlyEnabled) {
            // Remove App Gallery tile if overflow only is turned off since links will appear in App Flyout for more apps.
            hubTiles.delete(HubTileID.appgallery);
        }
        if (!capabilities.myContentEnabled) {
            hubTiles.delete(HubTileID.mycontent);
        }
        if (!capabilities.feedEnabled) {
            hubTiles.delete(HubTileID.feed);
        }
        if (!((_d = capabilities.startPageAppsArray) === null || _d === void 0 ? void 0 : _d.includes('visio'))) {
            // Remove Visio if the Visio start page is not enabled.
            hubTiles.delete(HubTileID.visio);
        }
        if (!((_e = capabilities.startPageAppsArray) === null || _e === void 0 ? void 0 : _e.includes('whiteboard'))) {
            // Remove whiteboard if the Whiteboard start page is not enabled.
            hubTiles.delete(HubTileID.whiteboard);
        }
        if (!((_f = capabilities.startPageAppsArray) === null || _f === void 0 ? void 0 : _f.includes('stream'))) {
            // Remove Stream if the Stream start page is not enabled.
            hubTiles.delete(HubTileID.stream);
        }
        else {
            var streamWorkloadUrl = (_g = allWorkloads.find(function (workload) { return workload.Id === "ShellMicrosoftStream" /* FirstPartyWorkloadIds.Stream */; })) === null || _g === void 0 ? void 0 : _g.Url;
            // Stream Classic (i.e. not Stream 2.0) is enabled if the Stream workload URL does not contain the start page root path.
            var isStreamClassicEnabled = !(streamWorkloadUrl === null || streamWorkloadUrl === void 0 ? void 0 : streamWorkloadUrl.includes(PagePaths.StartPageRootPath));
            if (isStreamClassicEnabled) {
                // Remove Stream if Stream is not yet Stream 2.0, even if the Stream start page is enabled.
                hubTiles.delete(HubTileID.stream);
            }
        }
        if (!isMsa && !((_h = capabilities.startPageAppsArray) === null || _h === void 0 ? void 0 : _h.includes('clipchamp'))) {
            // Remove Clipchamp in commercial if the Clipchamp start page is not enabled.
            hubTiles.delete(HubTileID.clipchamp);
        }
        if (!isSubscribedUser && !hasConsent) {
            // Remove Defender tiles if user is free user and does not have consent based on AADC requirement
            hubTiles.delete(HubTileID.defender);
        }
        var pinnedIndex = 0;
        setHubAppData(Array.from(hubTiles).map(function (_a) {
            var _b = __read(_a, 2), tileID = _b[0], isPinned = _b[1];
            return ({
                id: tileID,
                pinnedIndex: isPinned ? pinnedIndex++ : undefined,
            });
        }));
    }, [
        defaultPinSetOverride,
        identity,
        userEntitlementsResult,
        workloads,
        capabilities,
        setHubAppData,
        (_c = (_b = featureGates === null || featureGates === void 0 ? void 0 : featureGates.result) === null || _b === void 0 ? void 0 : _b.orionGates) === null || _c === void 0 ? void 0 : _c.orionEnabled,
        isSubscribedUser,
        hasConsent,
    ]);
    return hubAppData;
};
//# sourceMappingURL=useDefaultHubApps.js.map